import React, { useContext } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { BookingPerson, getSpecialistsByName } from '@components/dashboard/data/Specialists';
import Colors from '@components/shared/Colors';
import { BodyLG, BodySM, BodySMBold } from '@components/shared/TextTypes';
import TwoColumn from '@components/shared/TwoColumn';
import { VMS_SPECIALIST_CARE_IMG_NO_BACKGROUND } from '@constants';
import { DigitalServiceType, PageSlice } from '@contracts';
import ReplacedText from './ReplacedText';

interface Props {
  slice: PageSlice;
}

const VMSSpecialistCarePrices: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { currency } = state;
  const specialistsData = getSpecialistsByName(DigitalServiceType.PPC, ['Sarah Montagu', 'Dr Linda Farahani', 'Natasha Evans']);
  const renderPrice = (item: BookingPerson) => {
    const price = Array.isArray(item.price) ? item.price.find((price) => price.currency === currency.letterCode)?.amount : item.price || 'N.A';
    const splitPrice = price?.split('.');
    const coins = splitPrice?.pop();
    const pounds = splitPrice?.pop();

    return (
      <PriceWrap>
        {pounds ? <PriceLabel>{t('components.from')}</PriceLabel> : null}
        <PriceFormat>
          {pounds ? (
            <>
              {currency.symbol} {pounds} <Superscript> {coins} </Superscript>
            </>
          ) : (
            <FreeLabel>{t('components.free')}</FreeLabel>
          )}
        </PriceFormat>
      </PriceWrap>
    );
  };

  const renderIconBox = (item: BookingPerson) => (
    <IconBox key={item.name}>
      <StyledSmallIcon src={item.picture} alt={item.name} />
      <TextPriceWrap>
        <IconTextWrap>
          <NameTitleWrap>
            <ReplacedText as={BodySMBold}>{item.name}</ReplacedText>
            <ReplacedText as={LightText}>{item.shortTitle}</ReplacedText>
          </NameTitleWrap>
          <ReplacedText as={BodySM}>{item.description}</ReplacedText>
          <ReplacedText as={BodySM}>{item.experience}</ReplacedText>
        </IconTextWrap>
        {renderPrice(item)}
      </TextPriceWrap>
    </IconBox>
  );

  return (
    <Container>
      <Inner>
        <TwoColumn gap="60px" mobileReverse={true} breakpoint="1024px" alignment="center">
          <TextWrap>
            <Title>{t('components.vms-specialist-care-prices.title')}</Title>
            <StyledBodyLG>{t('components.vms-specialist-care-prices.description')}</StyledBodyLG>
            <IconBoxWrap>{specialistsData.map((item) => item && renderIconBox(item))}</IconBoxWrap>
            <LightText>{t('components.vms-specialist-care-prices.note')}</LightText>
          </TextWrap>
          <StyledImg src={VMS_SPECIALIST_CARE_IMG_NO_BACKGROUND} alt="VMS Specialist Care" />
        </TwoColumn>
      </Inner>
    </Container>
  );
};

export default VMSSpecialistCarePrices;

const Container = styled.div`
  padding: 120px;

  @media (max-width: 1024px) {
    padding: 60px 30px 30px;
  }
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
  max-width: 480px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    align-items: center;
    gap: 30px;
  }
`;

const StyledSmallIcon = styled.img`
  width: 70px;
  height: 70px;
`;

const Title = styled.h3`
  font-family: 'Domaine';
  font-style: normal;
  font-weight: normal;
  font-size: 30px;
  line-height: 120%;
  color: inherit;

  @media (max-width: 1024px) {
    text-align: center;
    font-size: 24px;
  }
`;

const IconBoxWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  margin-bottom: 10px;
  gap: 20px;
  width: 100%;
`;

const IconBox = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  padding: 20px 0 0;
  color: ${Colors.navyBlue};
  border-top: 1px solid ${Colors.ecru};

  &:last-child {
    border-bottom: 1px solid ${Colors.ecru};
    padding-bottom: 20px;
  }

  @media (max-width: 1024px) {
    align-items: center;
    padding: 20px 0;

    &:first-child {
      padding-top: 20px;
    }
  }
`;

const IconTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 5px;

  & > span.fade {
    opacity: 0.5;
  }

  span {
    @media (max-width: 1024px) {
      font-size: 13px;
    }
  }
`;

const TextPriceWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 5px;

  @media (max-width: 1024px) {
    gap: 15px;
  }
`;

const PriceWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 3px;
  font-family: 'Domaine';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 110%;

  @media (max-width: 1024px) {
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const PriceFormat = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-start;
  flex: none;
  gap: 2px;
  padding: 0;
  font-family: 'Domaine';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 110%;
  width: max-content;
`;

const NameTitleWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;

  span {
    text-align: left;

    @media (max-width: 1024px) {
      font-size: 13px;
    }
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FreeLabel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 5px 8px;
  gap: 10px;
  background: #eba920;
  border-radius: 2px;
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 11px;
  line-height: 13px;
  text-align: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${Colors.cream};
`;

const PriceLabel = styled.div`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 100%;
  color: ${Colors.navyBlue};
  opacity: 0.5;
  width: 100%;
  text-align: end;

  @media (max-width: 1024px) {
    font-size: 10px;
    line-height: 150%;
  }
`;

const Superscript = styled.span`
  font-size: 12px;
  line-height: 110%;
  text-align: center;
`;

const StyledBodyLG = styled(BodyLG)`
  text-align: left;
  margin-bottom: 20px;
  font-size: 16px;
  color: inherit;

  @media (max-width: 1024px) {
    text-align: center;
    margin-bottom: 0px;
  }
`;

const StyledBodySM = styled(BodySM)`
  @media (max-width: 1024px) {
    text-align: center;
  }
`;

const StyledImg = styled.img`
  width: 100%;
  max-width: 424px;

  @media (max-width: 1024px) {
    max-width: 215px;
    margin-bottom: 40px;
  }
`;

const LightText = styled(StyledBodySM)`
  opacity: 0.5;
`;
